import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, formatApiError, setAuthToken, fmtItalianDate, fmtItalianTime, WEEKDAYS_IT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Calendar, Settings, Ban, Activity, Trash2, Download, Plus, MessageCircle, Mail, Link2, RefreshCw, Pencil, AlertTriangle, BarChart3, Hourglass, Check, X, BellRing, BellOff } from "lucide-react";
import EditAppointmentModal from "@/components/app/EditAppointmentModal";
import StatsPanel from "@/components/app/StatsPanel";
import ScheduledChangeEditor from "@/components/app/ScheduledChangeEditor";
import { pushSupported, getPushStatus, subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { useAdminManifest } from "@/lib/useAdminManifest";

function fmtDateTime(iso) {
  return `${fmtItalianDate(iso)} · ${fmtItalianTime(iso)}`;
}

function reminderWhatsappUrl(appt, prof, studio) {
  const n = (appt.phone || "").replace(/\D+/g, "");
  const day = fmtItalianDate(appt.start);
  const time = fmtItalianTime(appt.start);
  const msg = `Ciao ${appt.first_name}, ti ricordo il nostro appuntamento di ${day} alle ${time} presso ${studio?.name || "lo studio"}. A presto — ${prof || "Studio"}`;
  return `https://wa.me/${n}?text=${encodeURIComponent(msg)}`;
}

export default function AdminDashboard() {
  useAdminManifest();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [studios, setStudios] = useState([]);
  const [settings, setSettings] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [gstatus, setGstatus] = useState(null);
  const [newBlock, setNewBlock] = useState({ date: "", studio_id: "", reason: "" });
  const [loading, setLoading] = useState(true);
  const [editingAppt, setEditingAppt] = useState(null);
  const [apptFilter, setApptFilter] = useState("upcoming");
  const [pastAppointments, setPastAppointments] = useState([]);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "pending");
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const u = await api.get("/auth/me");
        setMe(u.data);
        await Promise.all([loadAppts(), loadStudios(), loadSettings(), loadBlocks(), loadGoogleStatus()]);
      } catch (e) {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
    if (pushSupported()) {
      getPushStatus().then(setPushStatus).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const g = searchParams.get("gcal");
    if (g === "connected") {
      toast.success(`Google Calendar collegato (${searchParams.get("email") || ""})`);
      loadGoogleStatus();
      searchParams.delete("gcal"); searchParams.delete("email");
      setSearchParams(searchParams, { replace: true });
    } else if (g === "error") {
      const reason = searchParams.get("reason") || "sconosciuto";
      const friendly = {
        access_denied: "Accesso negato. Se vedi 'app in fase di test', aggiungi il tuo account come Test user su Google Cloud Console → OAuth consent screen.",
        admin_policy_enforced: "L'amministratore del tuo account Workspace blocca l'app. Contatta l'admin del dominio.",
        redirect_uri_mismatch: "URL di redirect non corrispondente. Aggiungi l'URI di callback nella Google Cloud Console.",
        no_code: "Autorizzazione non completata.",
        not_configured: "OAuth non configurato lato server.",
        token_exchange: "Scambio token fallito. Verifica Client ID e Secret.",
        invalid_state: "Sessione OAuth scaduta. Riprova.",
      }[reason] || `Errore: ${reason}`;
      toast.error(friendly, { duration: 8000 });
      searchParams.delete("gcal"); searchParams.delete("reason");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const loadAppts = async () => {
    const [up, past, pending] = await Promise.all([
      api.get("/admin/appointments", { params: { filter: "upcoming" } }),
      api.get("/admin/appointments", { params: { filter: "past", limit: 200 } }),
      api.get("/admin/appointments", { params: { filter: "pending" } }),
    ]);
    setAppointments(up.data);
    setPastAppointments(past.data);
    setPendingAppointments(pending.data);
  };
  const loadStudios = async () => { const r = await api.get("/studios"); setStudios(r.data); };
  const loadSettings = async () => { const r = await api.get("/settings"); setSettings(r.data); };
  const loadBlocks = async () => { const r = await api.get("/admin/blocks"); setBlocks(r.data); };
  const loadGoogleStatus = async () => {
    try { const r = await api.get("/admin/google/status"); setGstatus(r.data); }
    catch { setGstatus({ configured: false, connected: false }); }
  };

  const connectGoogle = async () => {
    try {
      const r = await api.get("/admin/google/connect");
      if (r.data?.authorization_url) window.location.href = r.data.authorization_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnectGoogle = async () => {
    if (!window.confirm("Scollegare Google Calendar?")) return;
    try { await api.post("/admin/google/disconnect"); toast.success("Scollegato"); loadGoogleStatus(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const syncGoogle = async () => {
    try { const r = await api.post("/admin/google/sync"); toast.success(`Sincronizzati ${r.data.external_events} eventi esterni`); loadGoogleStatus(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const backfillGoogle = async () => {
    try {
      const r = await api.post("/admin/google/backfill");
      if (r.data.pushed > 0) toast.success(`${r.data.pushed} appuntament${r.data.pushed === 1 ? "o aggiunto" : "i aggiunti"} su Google Calendar`);
      else toast.info("Nessun appuntamento da sincronizzare: tutto già presente su Google Calendar");
      loadAppts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setAuthToken(null);
    navigate("/admin/login");
  };

  const deleteAppt = async (id) => {
    if (!window.confirm("Eliminare la prenotazione?")) return;
    try { await api.delete(`/admin/appointments/${id}`); toast.success("Eliminata"); loadAppts(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReminder = async (id) => {
    try { await api.post(`/admin/appointments/${id}/reminder`); toast.success("Promemoria email inviato"); loadAppts(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmAppt = async (id) => {
    try { await api.post(`/admin/appointments/${id}/confirm`); toast.success("Prenotazione confermata"); loadAppts(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rejectAppt = async (id) => {
    if (!window.confirm("Rifiutare questa richiesta? Il paziente riceverà una email.")) return;
    try { await api.post(`/admin/appointments/${id}/reject`); toast.success("Prenotazione rifiutata"); loadAppts(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus.subscribed) {
        await unsubscribeFromPush();
        toast.success("Notifiche push disattivate");
      } else {
        await subscribeToPush();
        toast.success("Notifiche push attivate su questo dispositivo");
      }
      const s = await getPushStatus();
      setPushStatus(s);
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche push");
    } finally {
      setPushBusy(false);
    }
  };

  const testEmail = async () => {
    try { const r = await api.post("/admin/test-email"); toast.success("Test email inviata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveStudio = async (s) => {
    try { await api.put(`/admin/studios/${s.id}`, s); toast.success(`${s.name} aggiornato`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addBlock = async () => {
    if (!newBlock.date) { toast.error("Inserisci una data"); return; }
    try {
      await api.post("/admin/blocks", { ...newBlock, studio_id: newBlock.studio_id || null });
      setNewBlock({ date: "", studio_id: "", reason: "" });
      toast.success("Giorno bloccato");
      loadBlocks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeBlock = async (id) => {
    await api.delete(`/admin/blocks/${id}`); loadBlocks();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("admin_token");
    const url = `${API}/admin/appointments/export${token ? `?_auth=${token}` : ""}`;
    // Use fetch to send Authorization header, then download
    fetch(`${API}/admin/appointments/export`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(r => r.blob()).then(b => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = "appuntamenti.csv"; a.click();
      URL.revokeObjectURL(u);
    });
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[#5C7066]">Caricamento…</div>;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#2D4A3E]/10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-[#2D4A3E] text-white grid place-items-center"><Activity size={16}/></span>
            <div>
              <div className="font-display text-base text-[#1A231F]">Pannello amministratore</div>
              <div className="text-xs text-[#5C7066]">{me?.email}</div>
            </div>
          </div>
          <Button data-testid="admin-logout" variant="ghost" onClick={logout} className="text-[#5C7066]"><LogOut size={16} className="mr-1"/>Esci</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        {pendingAppointments.length > 0 && activeTab !== "pending" && (
          <div data-testid="pending-banner" className="mb-6 rounded-xl border border-[#2D4A3E]/30 bg-[#2D4A3E]/5 p-4 flex items-start gap-3">
            <Hourglass size={20} className="text-[#2D4A3E] shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-medium text-[#1A231F]">{pendingAppointments.length} prenotazion{pendingAppointments.length===1?"e":"i"} in attesa di conferma</div>
              <div className="text-sm text-[#5C7066] mt-0.5">Conferma o rifiuta ogni richiesta prima che il paziente la veda confermata.</div>
              <button
                data-testid="pending-banner-btn"
                onClick={()=>setActiveTab("pending")}
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2D4A3E] text-white hover:bg-[#1A2F26] text-sm font-medium"
              ><Hourglass size={14}/> Vai alle richieste in attesa</button>
            </div>
          </div>
        )}

        {gstatus?.needs_reconnect && (
          <div data-testid="gcal-reconnect-banner" className="mb-6 rounded-xl border border-[#D4A373] bg-[#FEF6EC] p-4 flex items-start gap-3">
            <AlertTriangle size={20} className="text-[#B8730E] shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-medium text-[#1A231F]">Google Calendar da ricollegare</div>
              <div className="text-sm text-[#5C7066] mt-0.5">
                Google impone la ri-autorizzazione ogni 7 giorni finché l'app OAuth resta in modalità Testing. Le prenotazioni continuano ad essere salvate, ma non compaiono sul tuo calendario.
              </div>
              <button
                data-testid="gcal-reconnect-inline-btn"
                onClick={connectGoogle}
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2D4A3E] text-white hover:bg-[#1A2F26] text-sm font-medium"
              ><Link2 size={14}/> Ricollega ora</button>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E0E5E2] p-1 rounded-xl max-w-full overflow-x-auto justify-start">
            <TabsTrigger data-testid="tab-pending" value="pending" className="rounded-lg">
              <Hourglass size={14} className="mr-2"/>In attesa
              {pendingAppointments.length > 0 && (
                <span data-testid="pending-badge" className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-[#D4A373] text-white text-[11px] font-semibold">{pendingAppointments.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-appts" value="appts" className="rounded-lg"><Calendar size={14} className="mr-2"/>Prenotazioni</TabsTrigger>
            <TabsTrigger data-testid="tab-blocks" value="blocks" className="rounded-lg"><Ban size={14} className="mr-2"/>Ferie & Blocchi</TabsTrigger>
            <TabsTrigger data-testid="tab-stats" value="stats" className="rounded-lg"><BarChart3 size={14} className="mr-2"/>Statistiche</TabsTrigger>
            <TabsTrigger data-testid="tab-settings" value="settings" className="rounded-lg"><Settings size={14} className="mr-2"/>Studi & Impostazioni</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            <div className="mb-4">
              <h2 className="font-display text-xl text-[#1A231F]">Richieste in attesa di conferma</h2>
              <p className="text-sm text-[#5C7066] mt-1">Ogni nuova prenotazione resta bloccata qui finché non la confermi o la rifiuti. Riceverai un promemoria ogni 6 ore se non rispondi.</p>
            </div>
            {pendingAppointments.length === 0 ? (
              <div data-testid="pending-empty" className="card-soft p-6 text-center text-sm text-[#5C7066]">Nessuna richiesta in attesa. 🎉</div>
            ) : (
              <div className="space-y-3" data-testid="pending-list">
                {pendingAppointments.map(a => {
                  const stObj = studios.find(s=>s.id===a.studio_id);
                  return (
                    <div key={a.id} data-testid={`pending-row-${a.id}`} className="card-soft p-5 flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-medium text-[#1A231F]">{a.first_name} {a.last_name}</div>
                        <div className="text-sm text-[#5C7066] mt-1">{fmtDateTime(a.start)} · {stObj?.name || a.studio_id}</div>
                        <div className="text-sm text-[#5C7066] mt-1">{a.reason}</div>
                        <div className="text-xs text-[#5C7066] mt-1">{a.phone}{a.email?` · ${a.email}`:""}</div>
                        <div className="text-xs text-[#8AA399] mt-1">Richiesta il {fmtDateTime(a.created_at)}{a.pending_reminder_count>0 ? ` · sollecito ×${a.pending_reminder_count}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid={`pending-confirm-${a.id}`} onClick={()=>confirmAppt(a.id)} title="Conferma prenotazione" className="inline-flex items-center gap-1 text-white bg-[#2D4A3E] hover:bg-[#1A2F26] px-3 py-2 rounded-lg text-xs font-medium"><Check size={13}/>Conferma</button>
                        <button data-testid={`pending-reject-${a.id}`} onClick={()=>rejectAppt(a.id)} title="Rifiuta prenotazione" className="inline-flex items-center gap-1 text-[#B85C5C] border border-[#B85C5C] hover:bg-[#B85C5C] hover:text-white px-3 py-2 rounded-lg text-xs font-medium"><X size={13}/>Rifiuta</button>
                        <button data-testid={`pending-edit-${a.id}`} onClick={()=>setEditingAppt(a)} title="Modifica prenotazione" className="inline-flex text-[#2D4A3E] hover:bg-[#2D4A3E]/10 p-2 rounded-lg"><Pencil size={14}/></button>
                        <button data-testid={`pending-delete-${a.id}`} onClick={()=>deleteAppt(a.id)} title="Elimina" className="inline-flex text-[#B85C5C] hover:bg-[#B85C5C]/10 p-2 rounded-lg"><Trash2 size={14}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="appts" className="mt-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="inline-flex rounded-xl border border-[#E0E5E2] bg-white p-1" data-testid="appt-filter">
                  <button
                    data-testid="appt-filter-upcoming"
                    onClick={() => setApptFilter("upcoming")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${apptFilter==="upcoming" ? "bg-[#2D4A3E] text-white" : "text-[#5C7066] hover:text-[#2D4A3E]"}`}
                  >Prossime ({appointments.length})</button>
                  <button
                    data-testid="appt-filter-past"
                    onClick={() => setApptFilter("past")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${apptFilter==="past" ? "bg-[#2D4A3E] text-white" : "text-[#5C7066] hover:text-[#2D4A3E]"}`}
                  >Passate ({pastAppointments.length})</button>
                </div>
              </div>
              <Button data-testid="export-csv" onClick={exportCsv} variant="outline" className="rounded-xl border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white"><Download size={14} className="mr-2"/>Esporta CSV</Button>
            </div>
            <div data-testid="appt-table-scroll" className="card-soft overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
              <table className="w-full min-w-[820px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
                <thead className="bg-[#FAF9F6] text-left text-[#2D4A3E]">
                  <tr>
                    <th className="p-3">Quando</th>
                    <th className="p-3">Studio</th>
                    <th className="p-3">Paziente</th>
                    <th className="p-3">Motivo</th>
                    <th className="p-3">Contatti</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(apptFilter === "upcoming" ? appointments : pastAppointments).map(a => {
                    const stObj = studios.find(s=>s.id===a.studio_id);
                    const isPast = apptFilter === "past";
                    return (
                    <tr key={a.id} data-testid={`appt-row-${a.id}`} className="border-t border-[#E0E5E2]">
                      <td className="p-3 text-[#1A231F]">{fmtDateTime(a.start)}</td>
                      <td className="p-3 text-[#5C7066]">{stObj?.name || a.studio_id}</td>
                      <td className="p-3 text-[#1A231F]">{a.first_name} {a.last_name}</td>
                      <td className="p-3 text-[#5C7066]">{a.reason}</td>
                      <td className="p-3 text-[#5C7066]">{a.phone}{a.email?` · ${a.email}`:""}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {!isPast && (
                          <>
                            <button
                              data-testid={`appt-edit-${a.id}`}
                              onClick={()=>setEditingAppt(a)}
                              title="Modifica prenotazione"
                              className="inline-flex text-[#2D4A3E] hover:bg-[#2D4A3E]/10 p-2 rounded-lg"
                            ><Pencil size={16}/></button>
                            <a
                              data-testid={`appt-whatsapp-${a.id}`}
                              href={reminderWhatsappUrl(a, settings?.professional_name, stObj)}
                              target="_blank" rel="noopener noreferrer"
                              title="Invia promemoria WhatsApp (apre WhatsApp)"
                              className="inline-flex text-[#25D366] hover:bg-[#25D366]/10 p-2 rounded-lg"
                            ><MessageCircle size={16}/></a>
                            {a.email && (
                              <button
                                data-testid={`appt-email-${a.id}`}
                                onClick={()=>sendReminder(a.id)}
                                title={a.reminder_sent_at ? "Promemoria già inviato — reinvia" : "Invia promemoria email"}
                                className={`inline-flex ${a.reminder_sent_at?"text-[#8AA399]":"text-[#2D4A3E]"} hover:bg-[#2D4A3E]/10 p-2 rounded-lg`}
                              ><Mail size={16}/></button>
                            )}
                          </>
                        )}
                        <button data-testid={`appt-delete-${a.id}`} onClick={()=>deleteAppt(a.id)} className="inline-flex text-[#B85C5C] hover:bg-[#B85C5C]/10 p-2 rounded-lg"><Trash2 size={16}/></button>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
            {(apptFilter === "upcoming" ? appointments : pastAppointments).length === 0 && (
              <div data-testid="appt-empty" className="card-soft mt-3 p-6 text-center text-sm text-[#5C7066]">{apptFilter === "upcoming" ? "Nessuna prenotazione futura" : "Nessuna prenotazione passata registrata"}</div>
            )}
          </TabsContent>

          <TabsContent value="stats" className="mt-6">
            <StatsPanel />
          </TabsContent>

          <TabsContent value="blocks" className="mt-6">
            <h2 className="font-display text-xl text-[#1A231F] mb-4">Blocca un giorno (ferie/assenza)</h2>
            <div className="card-soft p-5 grid sm:grid-cols-4 gap-3 mb-6">
              <div>
                <Label>Data</Label>
                <Input data-testid="block-date" type="date" value={newBlock.date} onChange={(e)=>setNewBlock({...newBlock, date:e.target.value})} className="h-12 mt-1 rounded-xl"/>
              </div>
              <div>
                <Label>Studio</Label>
                <select data-testid="block-studio" value={newBlock.studio_id} onChange={(e)=>setNewBlock({...newBlock, studio_id:e.target.value})} className="h-12 mt-1 w-full rounded-xl border border-[#E0E5E2] bg-white px-3">
                  <option value="">Tutti gli studi</option>
                  {studios.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <Label>Motivo</Label>
                <Input data-testid="block-reason" value={newBlock.reason} onChange={(e)=>setNewBlock({...newBlock, reason:e.target.value})} className="h-12 mt-1 rounded-xl"/>
              </div>
              <div className="flex items-end">
                <Button data-testid="block-add" onClick={addBlock} className="h-12 w-full rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]"><Plus size={14} className="mr-1"/>Blocca</Button>
              </div>
            </div>

            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF9F6] text-left text-[#2D4A3E]">
                  <tr><th className="p-3">Data</th><th className="p-3">Studio</th><th className="p-3">Motivo</th><th className="p-3"></th></tr>
                </thead>
                <tbody>
                  {blocks.length===0 && <tr><td colSpan={4} className="p-6 text-center text-[#5C7066]">Nessun giorno bloccato</td></tr>}
                  {blocks.map(b=>(
                    <tr key={b.id} className="border-t border-[#E0E5E2]">
                      <td className="p-3 text-[#1A231F]">{b.date}</td>
                      <td className="p-3 text-[#5C7066]">{b.studio_id ? (studios.find(s=>s.id===b.studio_id)?.name || b.studio_id) : "Tutti"}</td>
                      <td className="p-3 text-[#5C7066]">{b.reason || "-"}</td>
                      <td className="p-3 text-right"><button data-testid={`block-delete-${b.id}`} onClick={()=>removeBlock(b.id)} className="text-[#B85C5C] hover:bg-[#B85C5C]/10 p-2 rounded-lg"><Trash2 size={16}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-8">
            <section className="card-soft p-6" data-testid="push-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display text-xl text-[#1A231F]">Notifiche push</h2>
                  <p className="text-sm text-[#5C7066] mt-1">
                    {!pushStatus.supported
                      ? "Il tuo browser non supporta le notifiche push."
                      : pushStatus.subscribed
                        ? "Attive su questo dispositivo: riceverai un avviso ad ogni nuova prenotazione e ogni 6 ore se resta da confermare."
                        : "Attiva le notifiche per essere avvisato subito su questo dispositivo quando arriva una nuova richiesta."}
                  </p>
                </div>
                {pushStatus.supported && (
                  <Button
                    data-testid="push-toggle-btn"
                    onClick={togglePush}
                    disabled={pushBusy}
                    variant={pushStatus.subscribed ? "outline" : "default"}
                    className={pushStatus.subscribed ? "h-11 rounded-xl border-[#B85C5C] text-[#B85C5C]" : "h-11 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]"}
                  >
                    {pushStatus.subscribed ? <><BellOff size={14} className="mr-2"/>Disattiva</> : <><BellRing size={14} className="mr-2"/>Attiva notifiche</>}
                  </Button>
                )}
              </div>
            </section>

            {gstatus && (
              <section className="card-soft p-6" data-testid="gcal-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-display text-xl text-[#1A231F]">Google Calendar</h2>
                    <p className="text-sm text-[#5C7066] mt-1">
                      {gstatus.needs_reconnect
                        ? `⚠️ Autorizzazione scaduta (Google la richiede ogni 7 giorni per app in Testing). Ricollega per riprendere la sincronizzazione.`
                        : gstatus.connected
                          ? `Collegato come ${gstatus.email}. Eventi esterni bloccati: ${gstatus.external_busy_events}.`
                          : gstatus.configured
                            ? "Collega il tuo Google Calendar: nuove prenotazioni saranno aggiunte automaticamente e gli eventi già presenti bloccheranno gli slot."
                            : "Google OAuth non configurato (mancano le variabili d'ambiente)."}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {gstatus.needs_reconnect ? (
                      <Button data-testid="gcal-reconnect-btn" onClick={connectGoogle} className="h-11 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]"><Link2 size={14} className="mr-2"/>Ricollega</Button>
                    ) : gstatus.connected ? (
                      <>
                        <Button data-testid="gcal-sync-btn" variant="outline" onClick={syncGoogle} className="h-11 rounded-xl border-[#2D4A3E] text-[#2D4A3E]"><RefreshCw size={14} className="mr-2"/>Sincronizza ora</Button>
                        <Button data-testid="gcal-backfill-btn" variant="outline" onClick={backfillGoogle} className="h-11 rounded-xl border-[#2D4A3E] text-[#2D4A3E]"><RefreshCw size={14} className="mr-2"/>Ripristina mancanti</Button>
                        <Button data-testid="gcal-disconnect-btn" variant="outline" onClick={disconnectGoogle} className="h-11 rounded-xl border-[#B85C5C] text-[#B85C5C]">Scollega</Button>
                      </>
                    ) : gstatus.configured ? (
                      <Button data-testid="gcal-connect-btn" onClick={connectGoogle} className="h-11 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]"><Link2 size={14} className="mr-2"/>Collega Google Calendar</Button>
                    ) : null}
                  </div>
                </div>
              </section>
            )}

            {settings && (
              <section className="card-soft p-6">
                <h2 className="font-display text-xl text-[#1A231F] mb-1">Profilo professionista</h2>
                <p className="text-xs text-[#5C7066] mb-4">Le modifiche a questa sezione aggiornano subito la home, la pagina di prenotazione e le email di conferma/reminder.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Nome</Label><Input data-testid="settings-name" value={settings.professional_name||""} onChange={e=>setSettings({...settings, professional_name:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                  <div><Label>Qualifica</Label><Input data-testid="settings-qualification" value={settings.qualification||""} onChange={e=>setSettings({...settings, qualification:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                  <div><Label>WhatsApp (es. +393331234567)</Label><Input data-testid="settings-whatsapp" value={settings.whatsapp_number||""} onChange={e=>setSettings({...settings, whatsapp_number:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                  <div><Label>Durata standard (min)</Label><Input data-testid="settings-duration" type="number" value={settings.default_duration||45} onChange={e=>setSettings({...settings, default_duration:parseInt(e.target.value)||45})} className="h-12 mt-1 rounded-xl"/></div>
                  <div className="sm:col-span-2"><Label>Bio (mostrata in home)</Label><Textarea data-testid="settings-bio" rows={3} value={settings.bio||""} onChange={e=>setSettings({...settings, bio:e.target.value})} className="mt-1 rounded-xl"/></div>
                  <div className="sm:col-span-2"><Label>URL immagine hero</Label><Input data-testid="settings-image" value={settings.hero_image||""} onChange={e=>setSettings({...settings, hero_image:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                  <div><Label>Sito web (link mostrato in conferma)</Label><Input data-testid="settings-website" value={settings.website_url||""} onChange={e=>setSettings({...settings, website_url:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                  <div><Label>Email per notifiche</Label><Input data-testid="settings-notify-email" type="email" value={settings.notify_email||""} onChange={e=>setSettings({...settings, notify_email:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                  <div className="sm:col-span-2"><Label>URL recensioni Google</Label><Input data-testid="settings-review" value={settings.google_review_url||""} onChange={e=>setSettings({...settings, google_review_url:e.target.value})} className="h-12 mt-1 rounded-xl"/></div>
                </div>

                <div className="mt-8 pt-6 border-t border-[#E0E5E2]">
                  <h3 className="font-display text-lg text-[#1A231F] mb-1">Messaggi personalizzati</h3>
                  <p className="text-xs text-[#5C7066] mb-4">Compaiono in un riquadro sabbia sulla pagina di conferma e dentro le email al paziente. Usa questo spazio per: indicazioni sull'arrivo, cosa portare, parcheggio, orari citofono, ecc.</p>
                  <div className="space-y-4">
                    <div>
                      <Label>Messaggio in conferma prenotazione + email conferma</Label>
                      <Textarea data-testid="settings-custom-confirm" rows={4} value={settings.custom_message_confirm||""} onChange={e=>setSettings({...settings, custom_message_confirm:e.target.value})} className="mt-1 rounded-xl" placeholder="Es: All'arrivo suona al citofono 'DeRobertis' al piano 2. Parcheggio gratuito in via Bramante 43. Se sei in ritardo di più di 10 min avvisami su WhatsApp."/>
                    </div>
                    <div>
                      <Label>Messaggio nel reminder email (24h prima)</Label>
                      <Textarea data-testid="settings-custom-reminder" rows={3} value={settings.custom_message_reminder||""} onChange={e=>setSettings({...settings, custom_message_reminder:e.target.value})} className="mt-1 rounded-xl" placeholder="Es: Ricorda di portare eventuali referti recenti. A domani!"/>
                    </div>
                    <div>
                      <Label>Note in fondo alle email (partita IVA, contatti, disclaimer…)</Label>
                      <Textarea data-testid="settings-footer-note" rows={2} value={settings.footer_note||""} onChange={e=>setSettings({...settings, footer_note:e.target.value})} className="mt-1 rounded-xl" placeholder="Studio Osteopatia — Dott. Luca DeRobertis · P.IVA 12345678901 · Milano"/>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2 justify-end">
                  <Button data-testid="test-email-btn" variant="outline" onClick={testEmail} className="h-12 rounded-xl border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white"><Mail size={14} className="mr-2"/>Invia test email</Button>
                  <Button data-testid="settings-save" onClick={saveSettings} className="h-12 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]">Salva impostazioni</Button>
                </div>
              </section>
            )}

            {studios.map(s => (
              <section key={s.id} className="card-soft p-6">
                <h2 className="font-display text-xl text-[#1A231F]">{s.name}</h2>
                <p className="text-xs text-[#5C7066] mb-4">Modifica nome visualizzato, indirizzo, link Google Maps e orari. Le modifiche sono immediate e valgono per tutte le prenotazioni future.</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div><Label>Nome visualizzato</Label><Input data-testid={`studio-${s.id}-name`} value={s.name} onChange={e=>setStudios(studios.map(x=>x.id===s.id?{...x, name:e.target.value}:x))} className="h-12 mt-1 rounded-xl"/></div>
                  <div><Label>Indirizzo</Label><Input data-testid={`studio-${s.id}-address`} value={s.address} onChange={e=>setStudios(studios.map(x=>x.id===s.id?{...x, address:e.target.value}:x))} className="h-12 mt-1 rounded-xl"/></div>
                  <div className="sm:col-span-2"><Label>Google Maps URL</Label><Input data-testid={`studio-${s.id}-maps`} value={s.maps_url} onChange={e=>setStudios(studios.map(x=>x.id===s.id?{...x, maps_url:e.target.value}:x))} className="h-12 mt-1 rounded-xl"/></div>
                </div>
                <div className="mt-5">
                  <div className="overline mb-2">Orari di ricevimento</div>
                  <div className="space-y-2">
                    {s.schedule.map((sc, idx) => (
                      <div key={idx} className="grid sm:grid-cols-4 gap-2 items-center">
                        <select value={sc.weekday} onChange={e=>{ const ns={...s, schedule: s.schedule.map((x,i)=>i===idx?{...x, weekday: parseInt(e.target.value)}:x)}; setStudios(studios.map(x=>x.id===s.id?ns:x)); }} className="h-11 rounded-xl border border-[#E0E5E2] bg-white px-3">
                          {WEEKDAYS_IT.map((w,wi)=>(<option key={wi} value={wi}>{w}</option>))}
                        </select>
                        <Input type="time" value={sc.start} onChange={e=>{ const ns={...s, schedule: s.schedule.map((x,i)=>i===idx?{...x, start: e.target.value}:x)}; setStudios(studios.map(x=>x.id===s.id?ns:x)); }} className="h-11 rounded-xl"/>
                        <Input type="time" value={sc.last_start} onChange={e=>{ const ns={...s, schedule: s.schedule.map((x,i)=>i===idx?{...x, last_start: e.target.value}:x)}; setStudios(studios.map(x=>x.id===s.id?ns:x)); }} className="h-11 rounded-xl"/>
                        <button onClick={()=>{ const ns={...s, schedule: s.schedule.filter((_,i)=>i!==idx)}; setStudios(studios.map(x=>x.id===s.id?ns:x)); }} className="text-[#B85C5C] hover:underline text-sm text-left">Rimuovi</button>
                      </div>
                    ))}
                  </div>
                  <button onClick={()=>{ const ns={...s, schedule: [...s.schedule, {weekday: 0, start: "09:00", last_start: "12:00"}]}; setStudios(studios.map(x=>x.id===s.id?ns:x)); }} className="mt-3 text-sm text-[#2D4A3E] hover:underline">+ Aggiungi fascia</button>
                </div>
                <div className="mt-5 text-right">
                  <Button data-testid={`studio-${s.id}-save`} onClick={()=>saveStudio(s)} className="h-12 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]">Salva studio</Button>
                </div>
                <ScheduledChangeEditor studio={s} onSaved={loadStudios} />
              </section>
            ))}
          </TabsContent>
        </Tabs>
      </main>

      <EditAppointmentModal
        appt={editingAppt}
        studios={studios}
        open={!!editingAppt}
        onClose={() => setEditingAppt(null)}
        onSaved={() => loadAppts()}
      />
    </div>
  );
}
